import { Star } from "lucide-react";
import njccLogo from "@/assets/njcc-logo.webp";
import { REVIEW_COUNT } from "@/config/reviews";


const GOOGLE_REVIEWS_URL = "https://www.google.com/maps/place/Masonry+Pro+Construction/@40.9077604,-74.2174174,17z/data=!4m18!1m9!3m8!1s0x89c2fdfe4c9fca95:0xdad63374c7f86ff2!2sMasonry+Pro+Construction!8m2!3d40.9077564!4d-74.2148425!9m1!1b1!16s%2Fg%2F1ptz3yl87!3m7!1s0x89c2fdfe4c9fca95:0xdad63374c7f86ff2!8m2!3d40.9077564!4d-74.2148425!9m1!1b1!16s%2Fg%2F1ptz3yl87?utm_source=masonrypro&utm_medium=trust_strip&utm_campaign=homepage_trust";

const badgeLinkClass = "block no-underline transition-shadow duration-200 hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)] rounded-lg";

function NJLicensedBadge() {
  return (
    <a
      href="https://newjersey.mylicense.com/verification/Search.aspx?facility=Y"
      target="_blank"
      rel="noopener noreferrer"
      className={`${badgeLinkClass} rounded`}
    >
      <div className="flex w-[260px] h-[80px] bg-white border-2 border-[#0a2540] rounded overflow-hidden">
        {/* Left panel - inverted colors */}
        <div className="flex flex-col items-center justify-center w-[70px] bg-[#c9a961] text-[#0a2540] shrink-0">
          <svg aria-hidden="true" width="24" height="28" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 1L2 5v8c0 7.5 4.3 11.5 10 13 5.7-1.5 10-5.5 10-13V5L12 1z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M8 14l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
          <span className="text-[9px] tracking-[1px] mt-1 font-bold">VERIFIED</span>
        </div>
        {/* Right info */}
        <div className="flex flex-col justify-center px-3.5 py-2.5">
          <p className="text-[10px] tracking-[1px] font-medium text-[#666] leading-tight">NJ LICENSED CONTRACTOR</p>
          <p className="text-[13px] font-medium text-[#0a2540] mt-0.5 leading-tight">Home Improvement</p>
          <p className="text-[11px] text-[#333] mt-1 leading-tight">License #13VH06777200</p>
          <p className="text-[10px] text-[#c9a961] tracking-[0.5px] mt-0.5 font-medium leading-tight">Verify at NJ DCA →</p>
        </div>
      </div>
    </a>
  );
}

function ChamberBadge() {
  return (
    <a
      href="https://northjerseychamber.org/index.php/member-directory/#!biz/id/69df8b52a3db9ca46008be83"
      target="_blank"
      rel="noopener noreferrer"
      className={badgeLinkClass}
    >
      <div className="flex items-center w-[260px] h-[80px] bg-white border border-[#dadce0] rounded-lg px-[18px] py-[14px] box-border gap-3.5 overflow-hidden">
        <img
          src={njccLogo}
          alt="North Jersey Chamber of Commerce member"
          width="120"
          height="52"
          loading="lazy"
          className="h-12 w-auto object-contain shrink-0"
        />
        <div className="min-w-0 flex flex-col justify-center">
          <p className="text-[10px] tracking-[1px] font-medium text-[#5f6368] leading-tight">MEMBER</p>
          <p className="text-[12px] font-semibold text-[#2a3444] leading-tight">Chamber of Commerce</p>
          <p className="text-[11px] text-[#1a73e8] mt-1 leading-tight">View member profile →</p>
        </div>
      </div>
    </a>
  );
}

function BBBBadge() {
  return (
    <a
      href="https://www.bbb.org/us/nj/totowa/profile/construction/masonry-pro-construction-0221-90237395"
      target="_blank"
      rel="noopener noreferrer"
      className={badgeLinkClass}
    >
      <div className="flex items-center w-[260px] h-[80px] bg-white border border-[#dadce0] rounded-lg px-[18px] py-[14px] box-border gap-3.5">
        <div className="pr-3.5 border-r border-[#e8eaed]">
          <div className="bg-[#015A75] text-white font-bold text-[16px] px-3 py-2 rounded tracking-[1px]">
            BBB
          </div>
        </div>
        <div className="flex flex-col justify-center">
          <div className="flex items-center gap-1">
            <span className="text-[22px] font-medium text-[#015A75]">A+</span>
            <span className="text-[13px] text-[#5f6368] ml-1">Rating</span>
          </div>
          <p className="text-[12px] text-[#5f6368] mt-0.5 leading-tight">Better Business Bureau</p>
          <p className="text-[11px] text-[#015A75] mt-1 leading-tight">View BBB Profile →</p>
        </div>
      </div>
    </a>
  );
}

function GoogleReviewsBadge() {
  return (
    <a
      href={GOOGLE_REVIEWS_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={badgeLinkClass}
    >
      <div className="flex items-center w-[260px] h-[80px] bg-white border border-[#dadce0] rounded-lg px-[18px] py-[14px] box-border gap-3.5">
        <div className="pr-3.5 border-r border-[#e8eaed]">
          <svg aria-hidden="true" width="32" height="32" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
        </div>
        <div className="flex flex-col justify-center">
          <div className="flex items-center gap-1">
            <span className="text-[18px] font-medium text-[#202124]">5.0</span>
            <div className="flex gap-px">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-[#FBBC05] text-[#FBBC05]" aria-hidden="true" />
              ))}
            </div>
          </div>
          <p className="text-[12px] text-[#5f6368] mt-0.5 leading-tight">{`${REVIEW_COUNT}+ Google Reviews`}</p>
          <p className="text-[11px] text-[#1a73e8] mt-1 leading-tight">Read all reviews →</p>
        </div>
      </div>
    </a>
  );
}

export function TrustVerificationStrip() {
  return (
    <section className="bg-[#2a3444] py-8 md:py-12 border-y border-border">
      <div className="container mx-auto px-4">
        <div className="text-center mb-6">
          <h3 className="text-xl md:text-2xl font-bold tracking-wide text-secondary uppercase">
            TRUSTED & VERIFIED
          </h3>
          <p className="text-[20px] text-white font-medium mt-1">
            Accredited Masonry Contractor in North Jersey
          </p>
        </div>

        <div className="flex justify-center">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-4 gap-3">
            <div className="flex justify-center">
              <NJLicensedBadge />
            </div>
            <div className="flex justify-center">
              <ChamberBadge />
            </div>
            <div className="flex justify-center">
              <BBBBadge />
            </div>
            <div className="flex justify-center">
              <GoogleReviewsBadge />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
